import React, { useState } from "react";
import styled from "styled-components";
import { CloseRounded, PlayCircleOutline, GetApp } from "@mui/icons-material";
import { Modal, Snackbar } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { liveEvents } from "../../data/constants";
import { sendEmail } from "../../utils/emailApi";

const Container = styled.div`
  background: linear-gradient(
    343.07deg,
    rgba(132, 59, 206, 0.06) 5.71%,
    rgba(132, 59, 206, 0) 64.83%
  );
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 50px 20px 100px;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1350px;
  padding: 10px 0 80px;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 700;
  text-align: center;
  color: #28ac30;
  background: linear-gradient(90deg, #28ac30, #85d67f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 1px 2px 4px rgba(0, 0, 0, 0.15);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 650px;
  color: ${({ theme }) => theme.text_secondary};

  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 16px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;
  width: 100%;
  margin-top: 40px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  width: 100%;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.card};
  box-shadow: rgba(23, 92, 230, 0.12) 0 4px 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  position: relative;
`;

const ImageWrap = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.bgLight};
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  aspect-ratio: 16 / 9;
  position: relative;

  @media (max-width: 1024px) {
    aspect-ratio: auto;
  }
`;

const EventImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: ${({ theme }) => theme.bgLight};
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background-color: ${({ $completed }) =>
    $completed ? "rgba(107, 114, 128, 0.9)" : "rgba(40, 172, 48, 0.9)"};
  color: white;
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px;
  flex: 1;
`;

const Meta = styled.div`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 15px;
  line-height: 1.5;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const EventTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.text_primary};
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
`;

const Description = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 16px;
  line-height: 1.55;
`;

const ActionButton = styled.button`
  margin-top: auto;
  width: max-content;
  min-width: 140px;
  padding: 12px 18px;
  border: 0;
  border-radius: 8px;
  background-color: ${({ theme, $secondary }) =>
    $secondary ? theme.text_secondary : theme.primary};
  color: ${({ theme }) => theme.white};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    font-size: 20px;
  }
`;

const RegisterButton = styled.button`
  margin-top: auto;
  width: max-content;
  min-width: 140px;
  padding: 12px 18px;
  border: 0;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  padding: 48px 0;
  text-align: center;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 18px;
`;

const ModalShell = styled.div`
  width: 100%;
  min-height: 100%;
  background-color: #000000a7;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 16px 0;
  box-sizing: border-box;
  overflow-y: auto;
`;

const FormPanel = styled.div`
  width: min(1180px, calc(100% - 24px));
  margin: auto 12px;
  max-height: calc(100vh - 32px);
  border-radius: 16px;
  background: ${({ theme }) => theme.card};
  color: ${({ theme }) => theme.text_primary};
  box-shadow: rgba(23, 92, 230, 0.18) 0 12px 34px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
`;

const FormHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(420px, 0.9fr);
  gap: 28px;
  padding: 28px;
  background: ${({ theme }) => theme.card};
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const FormImage = styled.img`
  width: 100%;
  height: 320px;
  object-fit: cover;
  background: ${({ theme }) => theme.bgLight};
  border-radius: 8px;

  @media (max-width: 980px) {
    height: auto;
    max-height: 240px;
    object-fit: contain;
  }
`;

const FormIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 28px;
  border-right: 3px solid ${({ theme }) => theme.primary};

  @media (max-width: 980px) {
    padding-right: 0;
    padding-bottom: 24px;
    border-right: 0;
    border-bottom: 3px solid ${({ theme }) => theme.primary};
  }
`;

const FormTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.text_primary};
  font-size: 32px;
  font-weight: 600;
  line-height: 1.25;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const DetailList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DetailItem = styled.div`
  border-radius: 8px;
  background: ${({ theme }) => theme.bgLight};
  padding: 12px 14px;
`;

const DetailLabel = styled.div`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 13px;
  line-height: 1.4;
`;

const DetailValue = styled.div`
  color: ${({ theme }) => theme.text_primary};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
`;

const DetailsHeading = styled.h3`
  margin: 8px 0 10px;
  color: ${({ theme }) => theme.text_primary};
  font-size: 20px;
  font-weight: 600;
`;

const DetailsText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 18px;
  line-height: 1.7;
`;

const VideoSection = styled.div`
  margin-top: 8px;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  border-radius: 12px;
  background: ${({ theme }) => theme.bgLight};
  box-shadow: rgba(23, 92, 230, 0.1) 0 4px 16px;
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 12px;
`;

const ResourcesSection = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 10px;
  background: ${({ theme }) => theme.primary};
  color: white;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
  box-shadow: rgba(23, 92, 230, 0.15) 0 4px 12px;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    font-size: 22px;
  }
`;

const Form = styled.form`
  padding: 26px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  border-radius: 12px;
  background: ${({ theme }) => theme.bgLight};
  box-shadow: rgba(23, 92, 230, 0.1) 0 4px 24px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    padding: 20px;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${({ theme }) => theme.text_primary};
  font-size: 14px;
  font-weight: 500;
`;

const FullField = styled(Field)`
  grid-column: 1 / -1;
`;

const Input = styled.input`
  width: 100%;
  padding: 13px 14px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #f8f8f8;
  color: ${({ theme }) => theme.text_primary};

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.primary + "20"};
    outline: none;
    background: white;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 13px 14px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #f8f8f8;
  color: ${({ theme }) => theme.text_primary};

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.primary + "20"};
    outline: none;
    background: white;
  }
`;

const StyledPhoneInput = styled(PhoneInput)`
  .react-tel-input {
    width: 100%;
    position: relative;
  }

  .form-control {
    width: 100%;
    height: auto;
    padding: 13px 14px 13px 52px;
    font-size: 16px;
    border: 1px solid #ccc;
    border-radius: 8px;
    background: #f8f8f8;
  }

  .form-control:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.primary + "20"};
  }

  .flag-dropdown {
    border-radius: 8px 0 0 8px;
  }

  .country-list {
    width: 320px;
    max-height: 240px;
    overflow-y: auto;
    z-index: 30;
    border-radius: 10px;
    box-shadow: rgba(15, 23, 42, 0.18) 0 12px 28px;
  }
`;

const CheckboxLabel = styled.label`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 15px;
  line-height: 1.5;
`;

const FormActions = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
`;

const SubmitButton = styled(RegisterButton)`
  margin-top: 0;
`;

const SuccessPanel = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px;
  border-radius: 12px;
  background: rgba(40, 172, 48, 0.08);
  border: 1px solid rgba(40, 172, 48, 0.18);
`;

const SuccessTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.text_primary};
  font-size: 22px;
  font-weight: 600;
`;

const WhatsAppSection = styled.div`
  padding: 18px 20px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(40, 172, 48, 0.14);
`;

const WhatsAppText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 15px;
  line-height: 1.6;
`;

const WhatsAppLink = styled.a`
  color: #1d8f2a;
  font-weight: 700;
  text-decoration: none;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 16px;
  border: 0;
  background: #ffffff;
  color: ${({ theme }) => theme.text_primary};
  border-radius: 999px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: rgba(0, 0, 0, 0.12) 0 4px 16px;
`;

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  jobTitle: "",
  country: "",
  attendeeType: "",
  consent: false,
};

const formatNigerianPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? `+${digits}` : "";
};

const getEventDetailParagraphs = (details) =>
  Array.isArray(details) ? details.filter(Boolean) : details ? [details] : [];

const extractYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
  );
  return match ? match[1] : null;
};

const WHATSAPP_EVENT_TITLE =
  "Front End Engineering Design of Oil and Gas Projects: Critical Factors for Project Success";

const WHATSAPP_GROUP_LINK =
  "https://chat.whatsapp.com/L0jyISQ3SOMBCwO2VbycyM?mode=gi_t";

const EventsWebinars = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [phone, setPhone] = useState("");
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState("");
  const [registrationComplete, setRegistrationComplete] = useState(false);

  const openRegistration = (event) => {
    setSelectedEvent(event);

    // ✅ Track user intent (clicked register)
    if (window.fbq) {
      window.fbq("track", "InitiateCheckout", {
        content_name: event.title,
      });
    }

    setFormData(initialFormData);
    setPhone("");
    setRegistrationComplete(false);
  };

  const closeRegistration = () => {
    if (!isSubmitting) {
      setSelectedEvent(null);
      setRegistrationComplete(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedEvent) return;

    setIsSubmitting(true);
    try {
      await sendEmail("webinarRegistration", {
        ...formData,
        phone: formatNigerianPhone(phone),
        eventTitle: selectedEvent.title,
        eventDate: selectedEvent.date,
        eventTime: selectedEvent.time,
        eventRegion: selectedEvent.region,
        eventType: selectedEvent.type,
      });

      // ✅ Track successful registration (conversion)
      if (window.fbq) {
        window.fbq("track", "Lead", {
          content_name: selectedEvent.title,
        });
      }

      setSnackbar(
        "Registration submitted. Please check your email for confirmation.",
      );
      setRegistrationComplete(true);
      setFormData(initialFormData);
      setPhone("");
    } catch (error) {
      console.error("Failed to register for event:", error);
      alert("Could not submit your registration. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container id="events">
      <Wrapper>
        <Title>Events and Webinars</Title>
        <Desc>
          Join upcoming Mags Engineering webinars for practical engineering
          training, project delivery guidance, and live technical discussions.
        </Desc>

        <Grid>
          {liveEvents.map((event) => (
            <Card key={event.id}>
              <ImageWrap>
                <EventImage src={event.image} alt="" />
                <StatusBadge $completed={event.completed}>
                  {event.completed ? "Completed" : "Upcoming"}
                </StatusBadge>
              </ImageWrap>
              <CardBody>
                <Meta>
                  <span>{event.date}</span>
                </Meta>
                <EventTitle>{event.title}</EventTitle>
                <Description>{event.description}</Description>
                {event.completed ? (
                  <ActionButton
                    type="button"
                    onClick={() => openRegistration(event)}
                    $secondary
                  >
                    <PlayCircleOutline />
                    View Recording
                  </ActionButton>
                ) : (
                  <RegisterButton
                    type="button"
                    onClick={() => openRegistration(event)}
                  >
                    Register
                  </RegisterButton>
                )}
              </CardBody>
            </Card>
          ))}

          {liveEvents.length === 0 && <EmptyState>No events found.</EmptyState>}
        </Grid>
      </Wrapper>

      <Modal open={Boolean(selectedEvent)} onClose={closeRegistration}>
        <ModalShell>
          {selectedEvent && (
            <FormPanel>
              <CloseButton
                type="button"
                onClick={closeRegistration}
                aria-label="Close form"
              >
                <CloseRounded fontSize="small" />
              </CloseButton>

              <FormHeader>
                <FormIntro>
                  <FormImage src={selectedEvent.image} alt="" />
                  <FormTitle>{selectedEvent.title}</FormTitle>

                  <DetailList>
                    <DetailItem>
                      <DetailLabel>Date</DetailLabel>
                      <DetailValue>{selectedEvent.date}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Time</DetailLabel>
                      <DetailValue>{selectedEvent.time}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Status</DetailLabel>
                      <DetailValue>
                        {selectedEvent.completed ? "Completed" : "Upcoming"}
                      </DetailValue>
                    </DetailItem>
                  </DetailList>

                  <DetailsHeading>About this webinar</DetailsHeading>
                  {selectedEvent.description && (
                    <DetailsText>{selectedEvent.description}</DetailsText>
                  )}

                  {getEventDetailParagraphs(selectedEvent.details).map(
                    (paragraph, index) => (
                      <DetailsText
                        key={`${selectedEvent.id}-paragraph-${index}`}
                      >
                        {paragraph}
                      </DetailsText>
                    ),
                  )}
                </FormIntro>

                {/* RIGHT PANEL (Dynamic) */}
                {selectedEvent.completed ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    {selectedEvent.youtubeUrl && (
                      <VideoSection>
                        <DetailsHeading>Watch Recording</DetailsHeading>
                        <VideoContainer>
                          <VideoIframe
                            src={`https://www.youtube.com/embed/${extractYouTubeId(
                              selectedEvent.youtubeUrl,
                            )}`}
                            title={selectedEvent.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </VideoContainer>
                      </VideoSection>
                    )}

                    {selectedEvent.slidesUrl && (
                      <ResourcesSection>
                        <DetailsHeading>Resources</DetailsHeading>
                        <DownloadButton
                          href={selectedEvent.slidesUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <GetApp />
                          Download PowerPoint Slides
                        </DownloadButton>
                      </ResourcesSection>
                    )}

                    {/* Optional CTA */}
                    {/* <ActionButton
                      onClick={() =>
                        document
                          .getElementById("events")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                    >
                      <PlayCircleOutline />
                      See Upcoming Webinars
                    </ActionButton> */}
                  </div>
                ) : (
                  <Form onSubmit={handleSubmit}>
                    {registrationComplete ? (
                      <SuccessPanel>
                        <SuccessTitle>Registration successful</SuccessTitle>
                        <WhatsAppText>
                          Your registration has been received. Please check your
                          email for confirmation details.
                        </WhatsAppText>
                      </SuccessPanel>
                    ) : (
                      <>
                        <Field>
                          First Name*
                          <Input
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                          />
                        </Field>

                        <Field>
                          Last Name*
                          <Input
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                          />
                        </Field>

                        <FullField>
                          Email Address*
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </FullField>

                        <Field>
                          Phone Number*
                          <StyledPhoneInput
                            country="ng"
                            enableSearch
                            value={phone}
                            onChange={(value) => setPhone(value)}
                            inputProps={{ name: "phone", required: true }}
                          />
                        </Field>

                        <Field>
                          Country*
                          <Input
                            name="country"
                            value={formData.country}
                            onChange={handleInputChange}
                            required
                          />
                        </Field>

                        <Field>
                          Company or School
                          <Input
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                          />
                        </Field>

                        <Field>
                          Job Title or Course of Study
                          <Input
                            name="jobTitle"
                            value={formData.jobTitle}
                            onChange={handleInputChange}
                          />
                        </Field>

                        <FullField>
                          Which best describes you?
                          <Select
                            name="attendeeType"
                            value={formData.attendeeType}
                            onChange={handleInputChange}
                          >
                            <option value="">Select one</option>
                            <option value="Student">Student</option>
                            <option value="Working professional">
                              Working professional
                            </option>
                            <option value="Current Mags trainee/client">
                              Current Mags trainee/client
                            </option>
                            <option value="Other">Other</option>
                          </Select>
                        </FullField>

                        <CheckboxLabel>
                          <input
                            type="checkbox"
                            name="consent"
                            checked={formData.consent}
                            onChange={handleInputChange}
                            required
                          />
                          I agree to receive communication about this event and
                          related Mags Engineering training updates.
                        </CheckboxLabel>

                        <FormActions>
                          <SubmitButton type="submit" disabled={isSubmitting}>
                            {isSubmitting
                              ? "Submitting..."
                              : "Submit Registration"}
                          </SubmitButton>
                        </FormActions>
                      </>
                    )}
                  </Form>
                )}
              </FormHeader>
            </FormPanel>
          )}
        </ModalShell>
      </Modal>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </Container>
  );
};

export default EventsWebinars;
